import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Space, Spin, Typography } from 'antd'
import { filter, get, isEmpty, join, map, size, some } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import {
  FormButtons,
  HistoryLink,
  ReduxMetadataTree,
} from '../../../common/components/sections'
import {
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import {
  EVENT_METADATA_TYPES,
  METADATA_TYPES,
  PERMISSIONS,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useDocumentTitle,
  useLatestEventByEntityTypesQuery,
  useUserHasPermissions,
} from '../../../common/hooks'

import { renderDishTypeEvent } from '../../helpers'
import {
  useDeleteDishTypeMutation,
  useDishTypesQuery,
  useReplaceDishTypeMutation,
  useRevertReplaceDishTypeMutation,
  useSaveDishTypesMutation,
  useSetIsCurationEnabledDishTypeMutation,
} from '../../hooks'
import {
  addDishTypeNode,
  expandDishTypeMainTreeKeys,
  expandDishTypeSupportingTreeKeys,
  moveDishTypes,
  renameDishTypeNode,
  resetDishTypeTree,
  setDataForRenamingDishType,
} from '../../redux/actions'

const DishTypesPage = () => {
  const dispatch = useDispatch()
  useDocumentTitle('Dish Types')
  const [isMoveActive, setIsMoveActive] = useState(false)
  const [isReplaceActive, setIsReplaceActive] = useState(false)
  const eventEntityTypes = useMemo(() => [EVENT_METADATA_TYPES.DISH_TYPE], [])

  const {
    isLoading: isFetchLatestEventLoading,
    error: fetchLatestEventError,
    data: latestEvent,
    refetch: refetchLastEvent,
  } = useLatestEventByEntityTypesQuery(eventEntityTypes)

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    initialData,
  } = useDishTypesQuery()

  const [saveDishTypes, { isLoading: isSaveLoading, error: saveError }] =
    useSaveDishTypesMutation(refetchLastEvent)

  const [deleteDishType] = useDeleteDishTypeMutation(refetchLastEvent)

  const [setIsCurationEnabledDishType] =
    useSetIsCurationEnabledDishTypeMutation(refetchLastEvent)

  const [replaceDishType] = useReplaceDishTypeMutation(refetchLastEvent)

  const [revertReplaceDishType] =
    useRevertReplaceDishTypeMutation(refetchLastEvent)

  const {
    isDirty,
    data,
    dataForRenaming,
    mainTreeExpandedKeys,
    supportingTreeExpandedKeys,
  } = useSelector(state => state.dishTypes.dishTypes)

  const hasFullPermission = useUserHasPermissions([
    PERMISSIONS.METADATA_TREE.SAVE,
  ])

  const hasPermissionToViewDetails = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.GET,
  )

  const treeData = isDirty ? data : initialData

  const handleAdd = useCallback(
    node => dispatch(addDishTypeNode(node)),
    [dispatch],
  )

  const handleDelete = useCallback(
    ({ id }) => deleteDishType(id),
    [deleteDishType],
  )

  const handleRevertReplaceDishType = useCallback(
    ({ replaceToken }) => revertReplaceDishType(replaceToken),
    [revertReplaceDishType],
  )

  const handleSetIsCurationEnabled = useCallback(
    ({ id, isCurationEnabled }) =>
      setIsCurationEnabledDishType(id, isCurationEnabled),
    [setIsCurationEnabledDishType],
  )

  const handleRename = useCallback(
    ({ path, name }) => dispatch(renameDishTypeNode({ path, name })),
    [dispatch],
  )

  const handleSetDataForRenaming = useCallback(
    ({ id }) => dispatch(setDataForRenamingDishType({ id })),
    [dispatch],
  )

  const handleMove = useCallback(
    (nodesToMove, destinationNodes) => {
      const [destinationNode] = destinationNodes
      const levelToMoveUnder = destinationNode.level - 1

      const parentSelectedNodes = filter(
        nodesToMove,
        ({ parentId }) => !some(nodesToMove, ({ id }) => id === parentId),
      )

      const restrictedToMoveNodes = filter(parentSelectedNodes, node => {
        if (levelToMoveUnder > node.level) {
          return !isEmpty(node.children)
        }

        if (levelToMoveUnder === node.level) {
          return !isEmpty(
            node.level === 1
              ? get(node, 'children[0].children')
              : node.children,
          )
        }

        return false
      })

      if (!isEmpty(restrictedToMoveNodes)) {
        showErrorNotification({
          title: 'Move Error',
          description: (
            <div>
              The following nodes can&apos;t be moved:
              {map(restrictedToMoveNodes, ({ name }) => (
                <Typography.Paragraph style={{ margin: 0 }} key={name}>
                  {name};
                </Typography.Paragraph>
              ))}
            </div>
          ),
        })
      } else {
        dispatch(
          moveDishTypes({
            nodesToMove: parentSelectedNodes,
            destinationNode,
          }),
        )
      }
    },
    [dispatch],
  )

  const handleReplace = useCallback(
    async (nodesToReplace, replacementNodes) => {
      if (size(replacementNodes) > 1) {
        return showErrorNotification({
          title: 'Replace Error',
          description: "Can't Replace with more than 1 node",
        })
      }

      const [nodeToReplace] = nodesToReplace
      const [replacementNode] = replacementNodes

      if (nodeToReplace.isCurationEnabled) {
        return showErrorNotification({
          title: 'Replace Error',
          description:
            "The node can't be replaced as it's still enabled for curation",
        })
      }

      if (nodeToReplace.isIgnored && !replacementNode.isIgnored) {
        return showErrorNotification({
          title: 'Replace Error',
          description: "Ignored dish type can't be replaced by non ignored",
        })
      }

      if (replacementNode.isIgnored && !nodeToReplace.isIgnored) {
        return showErrorNotification({
          title: 'Replace Error',
          description: "Non ignored dish type can't be replaced by ignored",
        })
      }

      if (!isEmpty(replacementNode.replacedBy)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't replace as it's already being replaced by ${replacementNode.replacedBy[0].name}`,
        })
      }

      if (!isEmpty(nodeToReplace.replacedBy)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't be replaced as it's already being replaced by ${nodeToReplace.replacedBy[0].name}`,
        })
      }

      if (!isEmpty(nodeToReplace.replaces)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't be replaced as it's replacing other nodes ${join(
            map(nodeToReplace.replaces, 'name'),
            ',',
          )}`,
        })
      }

      await replaceDishType({
        idToReplace: nodeToReplace.id,
        replacementId: replacementNode.id,
      })

      return setIsReplaceActive(false)
    },
    [replaceDishType],
  )

  const handleResetTree = useCallback(
    () => dispatch(resetDishTypeTree()),
    [dispatch],
  )

  const handleSaveData = useCallback(
    () => saveDishTypes(data),
    [data, saveDishTypes],
  )

  useEffect(() => {
    if (saveError?.message || fetchError?.message || fetchLatestEventError) {
      showErrorNotification({
        title: 'Dish Type error',
        description:
          saveError?.message || fetchError?.message || fetchLatestEventError,
      })
    }
  }, [fetchError, fetchLatestEventError, saveError])

  return isFetchLoading ||
    isSaveLoading ||
    isFetchLatestEventLoading ||
    isEmpty(treeData) ? (
    <Spin />
  ) : (
    <>
      <StyledPageHeader>
        <StyledPageTitle>Dish Types</StyledPageTitle>
        <StyledPageActions>
          <StyledVerticalSpace>
            <Space>
              <UpdateDataText
                updatedAt={latestEvent?.createdAt}
                updatedBy={latestEvent?.createdBy?.name}
              />

              <HistoryLink
                renderEvent={renderDishTypeEvent}
                filterEventTypes
                eventMetadataTypes={eventEntityTypes}
              />
            </Space>
            <FormButtons
              permissions={[PERMISSIONS.METADATA_TREE.SAVE]}
              isDisabled={isMoveActive || isReplaceActive ? true : !isDirty}
              onReset={handleResetTree}
              onSave={handleSaveData}
            />
          </StyledVerticalSpace>
        </StyledPageActions>
      </StyledPageHeader>
      <ReduxMetadataTree
        disableLink={!hasPermissionToViewDetails}
        mainTreeExpandedKeys={mainTreeExpandedKeys}
        supportingTreeExpandedKeys={supportingTreeExpandedKeys}
        expandMainTree={expandDishTypeMainTreeKeys}
        expandSupportingTree={expandDishTypeSupportingTreeKeys}
        metadataType={METADATA_TYPES.DISH_TYPES}
        isDirty={isDirty}
        showSearch
        allowReplaceMany={false}
        treeData={treeData}
        editable={hasFullPermission}
        addNode={handleAdd}
        deleteNode={handleDelete}
        renameNode={handleRename}
        moveNodes={handleMove}
        replaceNode={handleReplace}
        revertReplaceNode={handleRevertReplaceDishType}
        setDataForRenaming={handleSetDataForRenaming}
        setIsCurationEnabled={handleSetIsCurationEnabled}
        dataForRenaming={dataForRenaming}
        isMoveActive={isMoveActive}
        setIsMoveActive={setIsMoveActive}
        isReplaceActive={isReplaceActive}
        setIsReplaceActive={setIsReplaceActive}
      />
    </>
  )
}

export default DishTypesPage
